import React, { useState } from 'react';
import { Heart, Copy, Check, ArrowRight, ArrowLeft, HeartHandshake } from 'lucide-react';
import { Modal, PrimaryButton } from '@/components/ui';
import { COLORS } from '@/data';
import { generateCoupleCode, generateAccessCode, now } from '@/lib/utils';
import { d1Upsert } from '@/lib/d1-storage';
import type { Reviewer, CoupleData } from '@/types';

interface CoupleSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  reviewer: Reviewer;
  onCoupleCreated: (coupleId: string, coupleData: CoupleData) => void;
}

export const CoupleSetupModal: React.FC<CoupleSetupModalProps> = ({
  isOpen,
  onClose,
  reviewer,
  onCoupleCreated,
}) => {
  const [partnerName, setPartnerName] = useState(reviewer.partnerName || '');
  const [yourName, setYourName] = useState('');
  const [coupleCode, setCoupleCode] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [step, setStep] = useState<'names' | 'experience' | 'complete'>('names');
  const [copied, setCopied] = useState<'share' | 'access' | null>(null);
  const [saving, setSaving] = useState(false);

  // Couple experience form
  const [experienceForm, setExperienceForm] = useState({
    interestedInAnonymousFeedback: '',
    comfortWithFeedback: '',
    wouldSubmitClips: '' as '' | 'yes' | 'maybe' | 'no',
    interestedInScheduledActivities: '',
    interestedInCuratedExperiences: '',
    hisSizeDescription: '',
    wetnessImportance: '',
    openToNewThings: '',
    coupleExperienceNotes: '',
  });

  const handleCreateCouple = async () => {
    if (!partnerName.trim()) return;

    setSaving(true);

    const newCoupleCode = generateCoupleCode();
    const newAccessCode = generateAccessCode();
    setCoupleCode(newCoupleCode);
    setAccessCode(newAccessCode);

    const coupleData: CoupleData = {
      coupleId: newCoupleCode,
      accessCode: newAccessCode,
      hisName: yourName.trim() || 'Partner A',
      herName: partnerName.trim(),
      setupAt: now(),
      preferences: {},
      tried: [],
      favorites: [],
      wouldSubmitClips: experienceForm.wouldSubmitClips || undefined,
      interestedInAnonymousFeedback: experienceForm.interestedInAnonymousFeedback || undefined,
      comfortWithFeedback: experienceForm.comfortWithFeedback || undefined,
      interestedInScheduledActivities: experienceForm.interestedInScheduledActivities || undefined,
      interestedInCuratedExperiences: experienceForm.interestedInCuratedExperiences || undefined,
      hisSizeDescription: experienceForm.hisSizeDescription || undefined,
      wetnessImportance: experienceForm.wetnessImportance || undefined,
      openToNewThings: experienceForm.openToNewThings || undefined,
      coupleExperienceNotes: experienceForm.coupleExperienceNotes || undefined,
    };

    await d1Upsert('couples', coupleData);

    setSaving(false);
    setStep('complete');
    onCoupleCreated(newCoupleCode, coupleData);
  };

  const copyCode = (type: 'share' | 'access') => {
    navigator.clipboard.writeText(type === 'share' ? coupleCode : accessCode);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleClose = () => {
    setStep('names');
    setPartnerName(reviewer.partnerName || '');
    setYourName('');
    setCoupleCode('');
    setAccessCode('');
    setCopied(null);
    setExperienceForm({
      interestedInAnonymousFeedback: '',
      comfortWithFeedback: '',
      wouldSubmitClips: '',
      interestedInScheduledActivities: '',
      interestedInCuratedExperiences: '',
      hisSizeDescription: '',
      wetnessImportance: '',
      openToNewThings: '',
      coupleExperienceNotes: '',
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 'names' ? 'Create Couple Profile' : step === 'experience' ? 'Your Couple Experience' : 'Couple Profile Created!'}
      size="md"
    >
      <div className="p-6">
        {step === 'names' && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <p className="text-zinc-300 text-sm">
                Set up your couple profile to access couple features like shared calendars,
                wishlists, and recommendations from friends.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Your name <span className="text-zinc-500">(optional)</span>
                </label>
                <input
                  type="text"
                  value={yourName}
                  onChange={e => setYourName(e.target.value)}
                  placeholder="e.g. Mike, Sarah..."
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Partner's name <span className="text-pink-400">*</span>
                </label>
                <input
                  type="text"
                  value={partnerName}
                  onChange={e => setPartnerName(e.target.value)}
                  placeholder="e.g. Alex, Jamie..."
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500"
                />
              </div>
            </div>

            <button
              onClick={() => setStep('experience')}
              disabled={!partnerName.trim()}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-zinc-900 disabled:opacity-50"
              style={{ backgroundColor: COLORS.lime }}
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === 'experience' && (
          <div className="space-y-5">
            <div className="flex items-center gap-3 mb-2">
              <HeartHandshake className="w-6 h-6 text-pink-400" />
              <div>
                <h3 className="text-white font-semibold">What's Possible For You Two</h3>
                <p className="text-zinc-400 text-xs">Help us tailor the experience for {yourName || 'you'} & {partnerName}</p>
              </div>
            </div>

            {/* What's possible pitch */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-pink-500/10 border border-pink-500/20 rounded-lg p-2">
                <p className="text-pink-400 font-semibold text-xs mb-0.5">Anonymous Feedback</p>
                <p className="text-zinc-400 text-[10px]">Honest, no-judgement feedback on your intimate experiences from someone who doesn't know you.</p>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-2">
                <p className="text-blue-400 font-semibold text-xs mb-0.5">Curated Date Nights</p>
                <p className="text-zinc-400 text-[10px]">Hand-picked activities based on your preferences, size, wetness, and what excites you.</p>
              </div>
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-2">
                <p className="text-green-400 font-semibold text-xs mb-0.5">Scheduled Activities</p>
                <p className="text-zinc-400 text-[10px]">Suggestions dropped into your calendar so you never run out of ideas.</p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-2">
                <p className="text-amber-400 font-semibold text-xs mb-0.5">Private Chat</p>
                <p className="text-zinc-400 text-[10px]">Two-way chat with your reviewer. Ask questions, share how things went.</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Would you like anonymous expert feedback on your experiences?</label>
                <select value={experienceForm.interestedInAnonymousFeedback} onChange={e => setExperienceForm({ ...experienceForm, interestedInAnonymousFeedback: e.target.value })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                  <option value="">Select...</option>
                  <option value="Yes, definitely">Yes, definitely</option>
                  <option value="Curious">Curious - open to trying</option>
                  <option value="Maybe later">Maybe later</option>
                  <option value="No thanks">No thanks</option>
                </select>
              </div>

              {(experienceForm.interestedInAnonymousFeedback === 'Yes, definitely' || experienceForm.interestedInAnonymousFeedback === 'Curious') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">How comfortable are you both with someone giving honest feedback?</label>
                    <select value={experienceForm.comfortWithFeedback} onChange={e => setExperienceForm({ ...experienceForm, comfortWithFeedback: e.target.value })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                      <option value="">Select...</option>
                      <option value="Very comfortable">Very comfortable - bring it on</option>
                      <option value="Somewhat comfortable">Somewhat comfortable - keep it constructive</option>
                      <option value="Nervous but open">Nervous but open to trying</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Would you submit clips of your experiences for feedback?</label>
                    <select value={experienceForm.wouldSubmitClips} onChange={e => setExperienceForm({ ...experienceForm, wouldSubmitClips: e.target.value as '' | 'yes' | 'maybe' | 'no' })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                      <option value="">Select...</option>
                      <option value="yes">Yes - we'd love honest feedback on our clips</option>
                      <option value="maybe">Maybe - depends on how anonymous it is</option>
                      <option value="no">No - just want suggestions, not feedback on us</option>
                    </select>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Would you like activities scheduled into your calendar?</label>
                <select value={experienceForm.interestedInScheduledActivities} onChange={e => setExperienceForm({ ...experienceForm, interestedInScheduledActivities: e.target.value })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                  <option value="">Select...</option>
                  <option value="Yes, weekly">Yes - weekly suggestions</option>
                  <option value="Yes, occasionally">Yes - fortnightly or so</option>
                  <option value="On demand">Just when we ask</option>
                  <option value="No">No thanks</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Want experiences curated to your specific preferences?</label>
                <p className="text-zinc-500 text-xs mb-2">Based on his size, her wetness, what excites you both, fantasies, etc.</p>
                <select value={experienceForm.interestedInCuratedExperiences} onChange={e => setExperienceForm({ ...experienceForm, interestedInCuratedExperiences: e.target.value })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                  <option value="">Select...</option>
                  <option value="Yes, the more personal the better">Yes - the more personal the better</option>
                  <option value="Yes, but keep it tasteful">Yes - but keep suggestions tasteful</option>
                  <option value="Generic is fine">Generic suggestions are fine</option>
                </select>
              </div>

              {experienceForm.interestedInCuratedExperiences && experienceForm.interestedInCuratedExperiences !== 'Generic is fine' && (
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4 space-y-4">
                  <p className="text-purple-400 text-sm font-medium">Help us curate for you</p>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">How would you describe his size?</label>
                    <select value={experienceForm.hisSizeDescription} onChange={e => setExperienceForm({ ...experienceForm, hisSizeDescription: e.target.value })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                      <option value="">Select...</option>
                      <option value="Small">On the smaller side</option>
                      <option value="Average">Average</option>
                      <option value="Above average">Above average</option>
                      <option value="Large">Large</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">How important is wetness/arousal to your enjoyment as a couple?</label>
                    <select value={experienceForm.wetnessImportance} onChange={e => setExperienceForm({ ...experienceForm, wetnessImportance: e.target.value })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                      <option value="">Select...</option>
                      <option value="Very important">Very important - the wetter the better</option>
                      <option value="Important">Important - makes a real difference</option>
                      <option value="Nice but not essential">Nice but not essential</option>
                      <option value="Not something we think about">Not something we think about</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">How open are you both to trying completely new things?</label>
                    <select value={experienceForm.openToNewThings} onChange={e => setExperienceForm({ ...experienceForm, openToNewThings: e.target.value })} className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white">
                      <option value="">Select...</option>
                      <option value="Very open - push our boundaries">Very open - push our boundaries</option>
                      <option value="Open but gradual">Open but take it gradual</option>
                      <option value="Stick to what we know mostly">Stick to what we know mostly</option>
                      <option value="Only tried and tested">Only tried and tested things</option>
                    </select>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Anything else you'd want from your couple experience?</label>
                <textarea value={experienceForm.coupleExperienceNotes} onChange={e => setExperienceForm({ ...experienceForm, coupleExperienceNotes: e.target.value })} placeholder="What would make this amazing for you two? Any specific things you'd love help with..." className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 h-20 resize-none" />
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep('names')} className="flex items-center gap-2 bg-zinc-700 text-zinc-300 px-5 py-3 rounded-xl">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <PrimaryButton
                onClick={handleCreateCouple}
                disabled={saving}
                className="flex-1"
              >
                {saving ? 'Creating...' : 'Create Couple Profile'}
              </PrimaryButton>
            </div>
          </div>
        )}

        {step === 'complete' && (
          <div className="space-y-6 text-center">
            <div>
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-green-500 to-lime-500 flex items-center justify-center mx-auto mb-4">
                <Check className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                Welcome, {yourName || 'Partner A'} & {partnerName}!
              </h3>
              <p className="text-zinc-400 text-sm">
                Save both codes - you'll need them!
              </p>
            </div>

            {/* Access Code - for login */}
            <div className="bg-pink-500/10 border border-pink-500/30 rounded-xl p-4">
              <p className="text-pink-400 text-xs font-semibold mb-1">ACCESS CODE (Keep Private!)</p>
              <p className="text-2xl font-mono font-bold text-pink-400 mb-2">{accessCode}</p>
              <p className="text-zinc-500 text-xs mb-2">Use this to log in to your couple account</p>
              <button
                onClick={() => copyCode('access')}
                className="flex items-center justify-center gap-2 mx-auto px-4 py-1.5 bg-pink-500/20 text-pink-400 rounded-lg hover:bg-pink-500/30 transition-colors text-sm"
              >
                <Copy className="w-3 h-3" />
                {copied === 'access' ? 'Copied!' : 'Copy Access Code'}
              </button>
            </div>

            {/* Sharing Code - for friends */}
            <div className="bg-lime-500/10 border border-lime-500/30 rounded-xl p-4">
              <p className="text-lime-400 text-xs font-semibold mb-1">SHARING CODE (Share with Friends)</p>
              <p className="text-2xl font-mono font-bold text-lime-400 mb-2">{coupleCode}</p>
              <p className="text-zinc-500 text-xs mb-2">Friends use this to recommend clips for you</p>
              <button
                onClick={() => copyCode('share')}
                className="flex items-center justify-center gap-2 mx-auto px-4 py-1.5 bg-lime-500/20 text-lime-400 rounded-lg hover:bg-lime-500/30 transition-colors text-sm"
              >
                <Copy className="w-3 h-3" />
                {copied === 'share' ? 'Copied!' : 'Copy Sharing Code'}
              </button>
            </div>

            <button
              onClick={handleClose}
              className="w-full py-3 rounded-xl font-semibold text-zinc-900 transition-transform hover:scale-105"
              style={{ backgroundColor: COLORS.lime }}
            >
              Start Exploring Together
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};
